/**
 * Authentication utilities
 *
 * Login is handled by the MMT global header ($header.js).
 * This module provides cookie-based auth state checks and helpers.
 */

import { getCookie } from './cookies';
import { isPatBypassActive } from './session';

// ============================================================================
// Constants
// ============================================================================

export const AUTH_COOKIE_NAME = 'mmt-auth';
export const UUID_COOKIE_NAME = 'uuid';

// ============================================================================
// Environment Detection
// ============================================================================

/**
 * Get the cookie domain for cross-subdomain sharing.
 * - '.makemytrip.com' for production
 * - '.mmt.ai' for staging
 * - undefined for localhost/development
 */
export function getCookieDomain(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const hostname = window.location.hostname;

  if (hostname.endsWith('.makemytrip.com') || hostname === 'makemytrip.com') {
    return '.makemytrip.com';
  }

  if (hostname.endsWith('.mmt.ai') || hostname === 'mmt.ai') {
    return '.mmt.ai';
  }

  return undefined;
}

/**
 * Detect if running in staging environment
 */
export function isStaging(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname;

  if (hostname.includes('staging') || hostname.includes('localhost') || hostname === '127.0.0.1') {
    return true;
  }

  if (hostname.endsWith('.mmt.ai') || hostname === 'mmt.ai') {
    return true;
  }

  const mmtEnv = import.meta.env['VITE_MMT_ENV'] as string | undefined;
  if (mmtEnv === 'staging' || mmtEnv === 'development') {
    return true;
  }

  return false;
}

// ============================================================================
// Auth Status Functions
// ============================================================================

/**
 * Check if user is currently logged in.
 * Returns true if user has a valid auth cookie or PAT bypass is active.
 */
export function isLoggedIn(): boolean {
  if (isPatBypassActive()) {
    return true;
  }

  const authCookie = getCookie(AUTH_COOKIE_NAME);
  return authCookie !== null && authCookie !== '';
}

/**
 * Get the user's UUID from cookies
 */
export function getUserUUID(): string | null {
  return getCookie(UUID_COOKIE_NAME);
}
